import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import type { OnSubmitCommentFn } from 'src/components';
import { AlineMarkdownEditor } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/AlineMarkdownEditor';
import type {
  AlineMarkdownEditorRef,
  MentionUserFilter,
} from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/types';
import { normalizeBlankMarkdown } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/utils/empty-paragraph';
import { getCommentMarkdownMentions } from 'src/components/comments/comment-markdown-mentions';
import { GrantMentionedUsersAccessDialog } from 'src/components/simple-editor/GrantMentionedUsersAccessDialog';
import { useMentionedAccountsWithoutAccessForUserIds } from 'src/components/simple-editor/hooks';
import type { MentionEntity } from 'src/components/simple-editor/types';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { IS_APPLE } from 'src/helpers/environment';
import { makeStyles } from 'tss-react/mui';

import { ActivityEditorFooter } from './ActivityEditorFooter';

const useStyles = makeStyles()({
  card: {
    display: 'flex',
    flexDirection: 'column',
  },
});

const SUBMIT_SHORTCUT_LABEL = IS_APPLE ? '⌘ + Enter' : 'Ctrl + Enter';
const PEOPLE_ONLY_TYPES = [Apollo.MentionEntitySearchType.User];

/** The activity lane a comment posts into, and who can be mentioned there. */
export interface ComposerLane {
  audience: Apollo.ActivityAudience;
  canMentionUser: MentionUserFilter;
  /** Only people can be mentioned: no Aline AI, intake form or other entities. */
  peopleOnly: boolean;
}

/** Reads or clears the unsent draft, e.g. to carry it into a dialog. */
export interface ActivityComposerHandle {
  clear: () => void;
  /** The trimmed markdown draft, or '' when the composer is blank. */
  getDraft: () => string;
}

interface ActivityComposerProps {
  /** Unique key for this composer's Plate editor, so it doesn't collide with reply boxes that
   *  share the same mention entity. */
  editorId: string;
  onSubmit: OnSubmitCommentFn;
  placeholder: string;
  workflowId: UUID;
  /** Shows the composer but blocks typing and sending, e.g. in a preview. */
  disabled?: boolean;
  /** Render the formatting toolbar detached above the editor instead of inline. */
  floatingToolbar?: boolean;
  lane?: ComposerLane;
  /** Handle an @aline-ai invocation; providing this also offers the "@aline-ai" mention. */
  onSubmitAI?: (prompt: string) => Promise<boolean>;
  /** Only people can be mentioned, whatever the lane. */
  peopleOnly?: boolean;
  ref?: React.Ref<ActivityComposerHandle>;
}

/**
 * The markdown comment composer for the activity feed. Without a `lane` the server picks the
 * comment's audience; with one the comment posts into that lane.
 */
export function ActivityComposer({
  disabled = false,
  editorId,
  floatingToolbar = false,
  lane,
  onSubmit,
  onSubmitAI,
  peopleOnly: isPeopleOnlySurface = false,
  placeholder,
  ref,
  workflowId,
}: ActivityComposerProps): JSX.Element | null {
  const { classes } = useStyles();
  const audience = lane?.audience;
  const currentUser = useSelectCurrentUser();
  const [sending, setSending] = useState(false);
  const editorRef = useRef<AlineMarkdownEditorRef>(null);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [emailsWithoutAccess, setEmailsWithoutAccess] = useState<string[]>([]);
  const [pendingMarkdown, setPendingMarkdown] = useState<string | null>(null);
  const peopleOnly = isPeopleOnlySurface || Boolean(lane?.peopleOnly);
  const allowAi = Boolean(onSubmitAI) && !peopleOnly;
  const inputBlocked = disabled || sending;
  const mentionEntity = useMemo(
    () =>
      ({
        __typename: 'Workflow',
        id: workflowId,
      }) satisfies MentionEntity,
    [workflowId]
  );
  const { canEditMentionEntity, getEmailsWithoutAccessForUserIds, mentionEntityOwner } =
    useMentionedAccountsWithoutAccessForUserIds({ mentionEntity });

  const clearEditor = useCallback(() => {
    editorRef.current?.clear();
    setEmailsWithoutAccess([]);
    setPendingMarkdown(null);
  }, []);

  const submitMarkdown = useCallback(
    async (trimmed: string, isAiInvocation: boolean) => {
      setSending(true);
      try {
        const submitted = isAiInvocation
          ? Boolean(await onSubmitAI?.(trimmed))
          : Boolean(await onSubmit(trimmed, audience ? { audience } : undefined));
        if (submitted) clearEditor();
      } catch {
        // Preserve the draft. Callers surface their own failure UI where needed.
      } finally {
        setSending(false);
      }
    },
    [audience, clearEditor, onSubmit, onSubmitAI]
  );

  const handleSubmit = useCallback(
    async (markdown: string) => {
      const trimmed = normalizeBlankMarkdown(markdown.trim());
      if (!trimmed) return;

      const isAiInvocation = allowAi && Boolean(editorRef.current?.getHasMentionType('aline-ai'));
      const { directMentionEmails, mentionedUserIds: userMentionIds } = getCommentMarkdownMentions(
        editorRef.current?.getEditor(),
        markdown
      );
      const nextEmailsWithoutAccess = isAiInvocation
        ? []
        : getEmailsWithoutAccessForUserIds(userMentionIds, directMentionEmails);

      if (nextEmailsWithoutAccess.length) {
        setEmailsWithoutAccess(nextEmailsWithoutAccess);
        setPendingMarkdown(trimmed);
        setAccessDialogOpen(true);
        return;
      }

      await submitMarkdown(trimmed, isAiInvocation);
    },
    [allowAi, getEmailsWithoutAccessForUserIds, submitMarkdown]
  );

  const handleSubmitAfterAccessDialog = useCallback(async () => {
    if (!pendingMarkdown) return;

    await submitMarkdown(pendingMarkdown, false);
  }, [pendingMarkdown, submitMarkdown]);

  useImperativeHandle(
    ref,
    () => ({
      clear: clearEditor,
      getDraft: () => normalizeBlankMarkdown(editorRef.current?.getMarkdown().trim() ?? ''),
    }),
    [clearEditor]
  );

  // Public users can't comment; render nothing rather than an orphan, no-op composer card.
  if (!currentUser) return null;

  return (
    <>
      <div className={classes.card}>
        <AlineMarkdownEditor
          key={editorId}
          ref={editorRef}
          bottomContent={
            <ActivityEditorFooter
              mentionDisabled={inputBlocked}
              mentionTooltip="Add context"
              submitDisabled={inputBlocked}
              submitTooltip={`${SUBMIT_SHORTCUT_LABEL} to submit comment`}
              onOpenMentions={() => editorRef.current?.openMentionPicker()}
              onSubmit={() => void handleSubmit(editorRef.current?.getMarkdown() ?? '')}
            />
          }
          disabled={disabled}
          floatingToolbar={floatingToolbar}
          hideToolbar={floatingToolbar}
          maxHeight={180}
          mentions={{
            allowAlineAiMention: allowAi,
            allowIntakeFormMention: !peopleOnly,
            enabledTypes: peopleOnly ? PEOPLE_ONLY_TYPES : undefined,
            filterUser: lane?.canMentionUser,
            workflowId,
          }}
          minHeight={56}
          placeholder={placeholder}
          showToolbarOnSelection={floatingToolbar}
          style={{ borderRadius: 12 }}
          submitDisabled={inputBlocked}
          toolbarStyle={floatingToolbar ? { backgroundColor: 'transparent' } : undefined}
          value=""
          onSubmit={handleSubmit}
        />
      </div>
      <GrantMentionedUsersAccessDialog
        emailsWithoutAccess={emailsWithoutAccess}
        mentionEntity={mentionEntity}
        mentionEntityOwner={mentionEntityOwner}
        open={accessDialogOpen}
        setOpen={setAccessDialogOpen}
        variant={canEditMentionEntity ? 'grant-access' : 'request-access'}
        onSubmit={handleSubmitAfterAccessDialog}
      />
    </>
  );
}
